import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ReplayIcon from 'mdi-react/ReplayIcon';
import ExportVariantIcon from 'mdi-react/ExportVariantIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import Api from '../../../util/api';
import {
  isLightColor,
  takeoverLight,
  textOnColor,
  TAKEOVER_FACTS,
} from '../takeoverUtils';
import { slideAction } from './SpotlightStoryPreview';

// The app's screen the design is drawn at; the preview scales it to fit
export const SCREEN_WIDTH = 390;
// Room for the title on the screen, and its widest letters (extra bold caps)
const TITLE_ROOM = 342;
const TITLE_LETTER = 0.7;
// Haze specks drifting up through the centre beam: x, y, size, seconds, delay
const DUST = [
  [168, 210, 2, 7, 0],
  [214, 250, 3, 9, 1.2],
  [190, 300, 2, 8, 2.5],
  [236, 330, 2, 6, 0.6],
  [150, 360, 3, 10, 3.1],
  [205, 395, 2, 7, 1.8],
  [175, 430, 2, 9, 4.2],
  [245, 420, 3, 8, 2.2],
  [130, 440, 2, 6, 0.3],
  [260, 270, 2, 10, 3.6],
  [196, 160, 2, 7, 5.0],
  [222, 460, 2, 8, 1.0],
];

// The club colour, for the floor glow, the button and the light. The admin
// has no club colour of its own (the app's theme is built into the app); the
// closest is the primary colour of the club's AI brand, when it has one.
// Asked once per club, however many previews are open.
const FALLBACK_CLUB_COLOR = '#0469DC';
const brandColors = {};
export const useClubColor = () => {
  const config = useConfig();
  const clubId = config?.id;
  const [color, setColor] = useState(null);
  useEffect(() => {
    if (!clubId) return undefined;
    let cancelled = false;
    if (!brandColors[clubId]) {
      brandColors[clubId] = Api.news
        .getAiBrand()
        .then((brand) => brand?.primaryColor || null)
        .catch(() => null);
    }
    brandColors[clubId].then((brandColor) => {
      if (!cancelled) setColor(brandColor);
    });
    return () => {
      cancelled = true;
    };
  }, [clubId]);
  return isLightColor(color) ? color : FALLBACK_CLUB_COLOR;
};

// The screen's scale in the space it has (the frame keeps the proportions)
export const useScale = (ref, initialWidth) => {
  const [scale, setScale] = useState(initialWidth / SCREEN_WIDTH);
  useEffect(() => {
    const frame = ref.current;
    if (!frame || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width) {
        setScale(entry.contentRect.width / SCREEN_WIDTH);
      }
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [ref]);
  return scale;
};

/**
 * A takeover as the app opens it full screen: a dark stage, two side beams
 * swinging in and the centre beam flickering on, the number or year (the
 * tile's highlight) drawn huge inside the light, the image lit with a rim
 * and a faint reflection, then the text and buttons. Replay runs it again;
 * with reduced motion it shows lit straight away.
 * Styles: .takeover-preview in scss/component/takeover-preview.scss.
 */
const SpotlightTakeoverPreview = ({ tile, compact }) => {
  const p = partial('HighlightBannerForm');
  const frameRef = useRef(null);
  const scale = useScale(frameRef, compact ? 170 : 260);
  const clubColor = useClubColor();
  // Replay: a new key mounts the scene again, which restarts it
  const [cycle, setCycle] = useState(0);

  const presentation = tile?.presentation || {};
  const image = tile?.headingImage?.croppedImage || tile?.headingImage?.preview;
  const card = presentation.imageStyle === 'CARD';
  const light = takeoverLight(presentation.lightColor, clubColor);
  const title = `${tile?.title || ''}`.trim();
  const mark = `${tile?.highlight || ''}`.trim();
  const facts = (presentation.facts || [])
    .map((fact) => `${fact || ''}`.trim())
    .filter(Boolean)
    .slice(0, TAKEOVER_FACTS);
  // The button opens the tile's content: its own label, else the app's
  const contentId = tile?.contentType?.value ?? tile?.contentType;
  const defaultAction = slideAction(tile?.content?.value, contentId);
  const action =
    `${presentation.actionLabel || ''}`.trim() ||
    (defaultAction ? p(defaultAction) : '');
  const more = `${presentation.moreLabel || ''}`.trim();
  // A long name gets smaller rather than leaving the screen
  const titleSize = Math.min(
    58,
    Math.floor(TITLE_ROOM / (TITLE_LETTER * Math.max(title.length, 1)))
  );
  const markSize = mark.length <= 2 ? 300 : Math.max(90, 620 / mark.length);

  return (
    <div
      className={`takeover-preview${compact ? ' takeover-preview--compact' : ''}`}
    >
      <div className="takeover-preview__frame" ref={frameRef}>
        <div
          className={`takeover-preview__screen${
            card ? ' takeover-preview__screen--card' : ''
          }`}
          style={{
            '--tk-club': clubColor,
            '--tk-on-club': textOnColor(clubColor),
            '--tk-light': light,
            transform: `scale(${scale})`,
          }}
        >
          <div className="takeover-preview__scene" key={cycle}>
            {/* The two side beams and their lamps, swinging in */}
            <span className="takeover-preview__beam takeover-preview__beam--left" />
            <span className="takeover-preview__beam takeover-preview__beam--right" />
            <span className="takeover-preview__lamp takeover-preview__lamp--left" />
            <span className="takeover-preview__lamp takeover-preview__lamp--right" />

            {/* The centre beam, with the number only inside it */}
            <div className="takeover-preview__centre" aria-hidden>
              <span className="takeover-preview__cone" />
              {mark && (
                <span className="takeover-preview__mark-clip">
                  <span
                    className="takeover-preview__mark"
                    style={{ fontSize: markSize }}
                  >
                    {mark}
                  </span>
                </span>
              )}
              <span className="takeover-preview__lamp takeover-preview__lamp--centre" />
              <span className="takeover-preview__halo" />
              <span className="takeover-preview__pool" />
              {DUST.map(([x, y, size, duration, delay]) => (
                <span
                  key={`${x}-${y}`}
                  className="takeover-preview__dust"
                  style={{
                    left: x,
                    top: y,
                    width: size,
                    height: size,
                    animationDuration: `${duration}s`,
                    animationDelay: `${delay}s`,
                  }}
                />
              ))}
            </div>

            {/* A faint reflection on the floor */}
            <div className="takeover-preview__reflection" aria-hidden>
              {image ? (
                <img src={image} alt="" />
              ) : (
                <span className="takeover-preview__figure" />
              )}
            </div>

            {/* The image, lit, with a rim of light round it */}
            <div
              className="takeover-preview__subject"
              role="img"
              aria-label={title || p('takeoverPreviewImage')}
            >
              <div className="takeover-preview__rim" aria-hidden>
                {image ? (
                  <img src={image} alt="" />
                ) : (
                  <span className="takeover-preview__figure" />
                )}
              </div>
              <div className="takeover-preview__lit" aria-hidden>
                {image ? (
                  <img src={image} alt="" />
                ) : (
                  <span className="takeover-preview__figure" />
                )}
              </div>
              {!image && (
                <span className="takeover-preview__placeholder">
                  {p(
                    card
                      ? 'takeoverPreviewNoImage-CARD'
                      : 'takeoverPreviewNoImage-CUTOUT'
                  )}
                </span>
              )}
            </div>

            <div className="takeover-preview__text">
              {presentation.eyebrow && (
                <span className="takeover-preview__eyebrow takeover-preview__rest">
                  {presentation.eyebrow}
                </span>
              )}
              <p className="takeover-preview__name">
                {presentation.firstLine && (
                  <span className="takeover-preview__first takeover-preview__rest">
                    {presentation.firstLine}
                  </span>
                )}
                <span
                  className="takeover-preview__title"
                  style={{ fontSize: titleSize, lineHeight: `${titleSize}px` }}
                >
                  {title || p('takeoverPreviewTitle')}
                </span>
              </p>
              <div className="takeover-preview__actions takeover-preview__rest">
                {facts.length > 0 && (
                  <ul className="takeover-preview__facts">
                    {/* Facts can repeat, so their place is in the key */}
                    {facts.map((fact, index) => (
                      <li key={`${index}-${fact}`}>{fact}</li>
                    ))}
                  </ul>
                )}
                {action && (
                  <span className="takeover-preview__button">{action}</span>
                )}
                {more && (
                  <span className="takeover-preview__more">
                    {more}
                    <ChevronRightIcon size={16} />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Outside the scene, so Replay keeps its focus */}
          <div className="takeover-preview__top">
            <span className="takeover-preview__icon" aria-hidden>
              <ChevronLeftIcon size={22} />
            </span>
            <span className="takeover-preview__top-end">
              <button
                type="button"
                className="takeover-preview__icon"
                aria-label={p('takeoverReplay')}
                title={p('takeoverReplay')}
                onClick={() => setCycle((current) => current + 1)}
              >
                <ReplayIcon size={20} />
              </button>
              <span className="takeover-preview__icon" aria-hidden>
                <ExportVariantIcon size={18} />
              </span>
            </span>
          </div>
        </div>
      </div>
      {!compact && (
        <p className="takeover-preview__caption">
          {p('takeoverPreviewCaption')}
        </p>
      )}
    </div>
  );
};

SpotlightTakeoverPreview.propTypes = {
  // The tile as the form has it: title, highlight, headingImage, content,
  // contentType and presentation
  tile: PropTypes.shape({}),
  // Smaller, side by side with others (the group's tiles)
  compact: PropTypes.bool,
};

SpotlightTakeoverPreview.defaultProps = {
  tile: null,
  compact: false,
};

export default SpotlightTakeoverPreview;
